"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, User, ArrowLeft, ChevronRight } from "lucide-react";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse py-12">
        <div className="mb-8 h-12 w-3/4 rounded-md bg-muted"></div>
        <div className="mb-8 flex gap-4">
          <div className="size-10 rounded-full bg-muted"></div>
          <div className="h-10 w-48 rounded-md bg-muted"></div>
        </div>
        <div className="mb-12 h-[400px] w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-semibold text-destructive">Error loading post</h2>
        <p className="mt-2 text-muted-foreground">{error}</p>
        <Link to="#" className="mt-6 inline-block">
          <Button variant="outline">
            <ArrowLeft className="mr-2 size-4" />
            Back to Journal
          </Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-semibold text-foreground">Post not found</h2>
        <p className="mt-2 text-muted-foreground">The article you are looking for does not exist.</p>
        <Link to="#" className="mt-6 inline-block">
          <Button variant="outline">
            <ArrowLeft className="mr-2 size-4" />
            Back to Journal
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl py-12 md:py-20">
      <div className="mb-8">
        <Link to="#" className="mb-6 inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="mr-2 size-4" />
          Back to Journal
        </Link>
        
        <h1 className="font-default text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>
        
        <div className="mt-8 flex flex-wrap items-center gap-6 border-y border-border py-4 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="size-10 border border-border">
                {post.author.avatar && post.author.avatar["48"] ? (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                ) : (
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="size-5" />
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <p className="font-medium text-foreground">By {post.author.name}</p>
                <p className="text-xs">Author</p>
              </div>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2 border-l border-border pl-6">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          )}

          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2 border-l border-border pl-6">
              <span className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground">
                {post.categories[0].name}
              </span>
            </div>
          )}
        </div>
      </div>

      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-xl shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover max-h-[600px]"
          />
        </div>
      )}

      <div 
        className="prose prose-lg prose-stone dark:prose-invert max-w-none text-foreground/90 leading-relaxed [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-semibold [&>h2]:mt-12 [&>h2]:mb-6 [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-4 [&>p]:mb-6 [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>img]:rounded-xl [&>img]:shadow-sm"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "No content available." }}
      />
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => (
        <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-border shadow-sm transition-shadow hover:shadow-md">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="size-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="flex-1">
            {post.categories && post.categories.length > 0 && (
              <span className="mb-2 text-xs font-medium text-primary">
                {post.categories[0].name}
              </span>
            )}
            <CardTitle className="font-default text-xl line-clamp-2">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent hover:text-primary">
                Read Article
                <ChevronRight className="size-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", per_page: 1 };
  const wp_query = new WP_Query(currentWPQueryParams);

  
  const related_query_params = { post_type: "post", per_page: 3 };
  const related_wp_query = new WP_Query(related_query_params);

  return (
    <section id="single-post-template-section" className="bg-background px-6 py-12 md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl">
        
        {}
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mx-auto mt-16 max-w-5xl border-t border-border pt-16 md:mt-24 md:pt-24">
          <div className="mb-10 flex items-center justify-between">
            <h2 className="font-default text-3xl font-bold text-foreground">More from the Journal</h2>
            <Link to="#">
              <Button variant="outline" className="hidden md:flex">
                View All Posts
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
          
          <div className="mt-8 flex justify-center md:hidden">
            <Link to="#" className="w-full">
              <Button variant="outline" className="w-full">
                View All Posts
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}